import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { NextFormComponent } from '@app/next/declarative-forms/components/declarative-form/next-form.component';
import {
  FormModalData,
  ModalComponent,
} from '@app/next/messaging/components/modal/modal.component';
import { RaiseNowModalService } from '@app/next/raisenow/components/raisenow-modal/raisenow-modal.service';
import { messageByKey } from '@app/shared/messaging/util/message-by-key';
import { FormActionEvent } from '@app/verify/feature/forms/components/form-page/form-page.component';
import { logger } from '@fmnts/common/log';
import { Modal } from '@fmnts/components/modal';
import { includedIn } from '@fmnts/core/collection';
import { ValidationStrategy } from '@fmnts/declarative-forms';
import { scrollToFirstErrorMessage } from '@fmnts/declarative-forms/common';
import {
  DonorDeviceSignalStore,
  SelfCheckoutStreamStore,
} from '@formunauts/self-checkout/data-access';
import { SelfCheckout } from '@formunauts/self-checkout/domain';
import { UpdateService } from '@formunauts/shared/bundle/data-access';
import { prepareFormData } from '@formunauts/shared/campaigning/data-access';
import { CampaignForm } from '@formunauts/shared/campaigning/domain';
import { LegacyFmntsApiError } from '@formunauts/shared/errors/domain';
import * as Sentry from '@sentry/angular';
import * as Arr from 'effect/Array';
import { pipe } from 'effect/Function';
import * as O from 'effect/Option';
import {
  BehaviorSubject,
  catchError,
  defer,
  EMPTY,
  filter,
  finalize,
  first,
  map,
  ReplaySubject,
  Subject,
  takeUntil,
  throwError,
  timer,
} from 'rxjs';
import {
  ClientOfflineModalComponent,
  ClientTimeoutModalComponent,
  CookiesDisabledModalComponent,
} from '../components/form-modals/self-checkout-modals.component';
import * as Utils from '../self-checkout-feature-utils';

enum ActionKeys {
  Submit = 'submitForm',
}

@Component({
  selector: 'verify-checkout-form',
  templateUrl: './checkout-form.component.html',
})
export class CheckoutFormComponent {
  @Input() validationStrategy = ValidationStrategy.OnChange;

  protected readonly donorDeviceStore = inject(DonorDeviceSignalStore);
  protected readonly selfCheckoutStore = inject(SelfCheckoutStreamStore);
  protected readonly route = inject(ActivatedRoute);
  protected readonly router = inject(Router);
  protected readonly rnModal = inject(RaiseNowModalService);
  protected readonly el = inject(ElementRef<HTMLElement>);
  protected readonly modal = inject(Modal);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly updateService = inject(UpdateService);

  protected selfCheckoutForm: SelfCheckout.SelfCheckoutForm;
  protected hash: SelfCheckout.Hash = this.selfCheckoutStore.hash();
  protected paymentStep: O.Option<SelfCheckout.SelfCheckoutPaymentStep> =
    O.none();
  protected readonly disabledActions: string[] = [];
  protected readonly isSubmitting$ = new BehaviorSubject<boolean>(false);
  protected readonly isUploading$ = new BehaviorSubject<boolean>(false);

  /** Emits whenever there is some result from raisenow modal. */
  protected readonly _rnModalResult = new Subject<'success' | 'cancel'>();
  private readonly _destroyed = new ReplaySubject<boolean>(1);
  private readonly _log = logger({ name: CheckoutFormComponent.name });

  constructor() {
    this.selfCheckoutForm = O.getOrThrow(
      this.selfCheckoutStore.retriveCheckoutFormStorage(this.hash),
    );
    this.donorDeviceStore.setLanguages(this.selfCheckoutForm.language);
    this.updateService.lockUpdates(this);
    this.destroyRef.onDestroy(() => {
      this.updateService.unlockUpdates(this);
    });

    toObservable(this.selfCheckoutStore.selfCheckoutForm)
      .pipe(filter(O.isSome), takeUntilDestroyed())
      .subscribe((checkoutForm) => {
        this.selfCheckoutForm = checkoutForm.value;
        this.donorDeviceStore.setLanguages(checkoutForm.value.language);
      });

    toObservable(this.selfCheckoutStore.checkoutDefaultResponseError)
      .pipe(filter(O.isSome), takeUntilDestroyed())
      .subscribe((error) => {
        this.isUploading$.next(false);
        this.showErrorModal(this.selfCheckoutForm, error.value);
      });
    toObservable(this.selfCheckoutStore.checkoutTamaroResponseError)
      .pipe(filter(O.isSome), takeUntilDestroyed())
      .subscribe((error) => {
        this.isUploading$.next(false);
        this.showErrorModal(this.selfCheckoutForm, error.value);
      });

    toObservable(this.selfCheckoutStore.checkoutData)
      .pipe(filter(O.isSome), takeUntilDestroyed())
      .subscribe((preparedCheckoutData) => {
        this.paymentStep = SelfCheckout.requiresPaymentStep(
          preparedCheckoutData.value,
        );
        this._upload(preparedCheckoutData.value, this.paymentStep);
      });
    toObservable(this.selfCheckoutStore.checkoutTamaroResponse)
      .pipe(filter(O.isSome), takeUntilDestroyed())
      .subscribe((result) => {
        this.isUploading$.next(false);
        const checkoutData = this.selfCheckoutStore.checkoutData();
        if (O.isSome(checkoutData) && O.isSome(this.paymentStep)) {
          this._awaitPaymentStep$(
            checkoutData.value,
            this.paymentStep.value,
            result.value,
          );
        }
      });
    toObservable(this.selfCheckoutStore.checkoutDefaultResponse)
      .pipe(filter(O.isSome), takeUntilDestroyed())
      .subscribe((checkoutResponse) => {
        this.isUploading$.next(false);
        this.showSuccessModal(this.selfCheckoutForm);
      });
    this._rnModalResult
      .pipe(
        first(),
        filter((result): result is 'success' => result === 'success'),
      )
      .subscribe(() => {
        this.showSuccessModal(this.selfCheckoutForm);
      });
  }

  handleAction(event: FormActionEvent) {
    const { action, formComponent } = event;
    switch (action) {
      case ActionKeys.Submit:
        return this.submitForm(formComponent);
      default:
        this._log.error(`Unknown action: ${action}`);
    }
  }

  private submitForm(formComponent: NextFormComponent) {
    if (this.isSubmitting$.value) {
      return EMPTY;
    }
    this.isSubmitting$.next(true);
    return formComponent
      .submit$()
      .pipe(
        catchError((e) => {
          scrollToFirstErrorMessage(this.el.nativeElement);
          this._log.info('the form is not valid');
          return EMPTY;
        }),
        map((formData) => {
          const data: SelfCheckout.PreparedSelfCheckoutFormPayload = {
            formData,
            payload: SelfCheckout.SelfCheckoutData(
              prepareFormData(
                formData,
                CampaignForm.getFieldsOnPage(this.selfCheckoutForm.form),
              ),
            ),
          };

          return data;
        }),
        finalize(() => {
          this.isSubmitting$.next(false);
        }),
      )
      .subscribe({
        next: (preparedData) => {
          this.selfCheckoutStore.setCheckoutData(preparedData);
        },
      });
  }
  private _upload(
    data: SelfCheckout.PreparedSelfCheckoutFormPayload,
    paymentStep: O.Option<SelfCheckout.SelfCheckoutPaymentStep>,
  ) {
    const isTamaroPayment = O.getOrNull(paymentStep) === 'tamaro';

    const start$ = defer(() => {
      this.isUploading$.next(true);
      // When using tamaro, we want to delay starting the upload
      // This gives the whole payment process some time to ensure that our backend
      // received the webhooks and therefore reduces the chance of duplicates.
      return timer(isTamaroPayment ? TAMARO_DELAY : 0);
    });
    start$.subscribe(() => {
      if (isTamaroPayment) {
        this.selfCheckoutStore.checkoutTamaro({
          preparedData: data,
          hash: this.hash,
        });
      } else {
        this.selfCheckoutStore.checkout({
          preparedData: data,
          hash: this.hash,
        });
      }
    });
  }

  private showSuccessModal(formComponent: SelfCheckout.SelfCheckoutForm) {
    const modal = formComponent.form.modals.find(
      messageByKey('verificationSuccessModal'),
    );
    if (!modal) {
      throw new Error(
        `Modal with key "verificationSuccessModal" can't be found.`,
      );
    }

    const ref = this.modal.open<ModalComponent, FormModalData, string>(
      ModalComponent,
      {
        minWidth: '25vw',
        disableClose: true,
        data: { modal },
      },
    );

    ref
      .afterOpened()
      .pipe(takeUntil(this._destroyed))
      .subscribe(() => {
        this.selfCheckoutStore.clearCheckoutFormStorage(this.hash);
      });
  }

  private showErrorModal(
    checkoutForm: SelfCheckout.SelfCheckoutForm,
    error: SelfCheckout.SelfCheckoutError,
  ) {
    switch (error._tag) {
      case 'ClientTimeoutError':
        return this.modal.open(ClientTimeoutModalComponent);

      case 'ClientOfflineError':
        return this.modal.open(ClientOfflineModalComponent);

      case 'SelfCheckoutCSRFError':
        Sentry.captureException(
          new LegacyFmntsApiError({
            httpError: {
              ...error.httpError,
              message: error.httpError.error.message,
            },
          }),
        );
        return this.modal.open(CookiesDisabledModalComponent);
      case 'SelfCheckoutCustomError': {
        const modal = Utils.findHttpModalOrGenericFallback(
          checkoutForm.form.modals,
          error instanceof SelfCheckout.SelfCheckoutCustomError
            ? (error.status ?? null)
            : null,
        );
        return this.modal.open(ModalComponent, {
          minWidth: '25vw',
          data: { modal },
        });
      }
    }
  }

  /**
   * Await completion of payment step.
   *
   * @returns
   * An `Observable` that emits once and completes when the post
   * upload steps are completed.
   */
  private _awaitPaymentStep$(
    data: SelfCheckout.PreparedSelfCheckoutFormPayload,
    paymentStep: SelfCheckout.SelfCheckoutPaymentStep,
    response: SelfCheckout.SelfCheckoutResponseTamaro,
  ): void {
    switch (paymentStep) {
      case 'tamaro':
        return this._startTamaroPayment(data, response);
    }
  }

  /**
   * Starts payment via tamaro widget.
   *
   * @param formData
   * @param payload
   *
   * @returns
   * Observable that emits once when there is a result from the raisenow
   * modal, then completes.
   */
  private _startTamaroPayment(
    data: SelfCheckout.PreparedSelfCheckoutFormPayload,
    response: SelfCheckout.SelfCheckoutResponseTamaro,
  ): void {
    const { tamaro } = response;
    if (!tamaro) {
      throwError(
        () =>
          new Error(
            `Tamaro Modal can't be started because of missing tamaro config`,
          ),
      );
    } else {
      const tamaroPaymentMethods = pipe(
        SelfCheckout.getTamaroPaymentMethodsForSelfCheckout(data),
        O.getOrElse(() => [] as ReadonlyArray<string>),
        Arr.filter(includedIn(tamaro.config.paymentMethods)),
      );

      // First open modal
      this.rnModal.open({
        ...data.formData,
        raisenow_tamaro_instance: tamaro.instance,
        raisenow_tamaro_config: {
          ...tamaro.config,
          // For now we need to adapt the tamaro widget payment method manually
          paymentMethods: tamaroPaymentMethods,
        },
      });
    }
  }
}

const TAMARO_DELAY = 5000;
